import React from 'react';
import { ChevronDown, Filter, X, AlertTriangle } from 'lucide-react';
import './ProjectionsFilters.css';
import { ReliabilityTier } from './projectionsModel';

export interface ProjectionFiltersState {
    minYield: number;
    maxPer: number | null;
    growthTarget: 'any' | 'div' | 'rev' | 'res' | 'all_growth';
    reliability: 'any' | ReliabilityTier;
    riskOnly: boolean;
}

export const FILTRES_PAR_DEFAUT: ProjectionFiltersState = {
    minYield: 0,
    maxPer: null,
    growthTarget: 'any',
    reliability: 'any',
    riskOnly: false,
};

export interface ProjectionsFiltersProps {
    filters: ProjectionFiltersState;
    onFiltersChange: (filters: ProjectionFiltersState) => void;
    /** Exercices présents en cache, du plus récent au plus ancien (GET /projections/financial/years). */
    annees: number[];
    annee: number | null;
    onAnneeChange: (annee: number) => void;
    totalResults: number;
    filteredResults: number;
}

const YIELD_OPTIONS = [
    { value: 0, label: 'Tous les rendements' },
    { value: 5, label: '> 5%' },
    { value: 7, label: '> 7%' },
    { value: 10, label: '> 10%' },
];

const PER_OPTIONS = [
    { value: null, label: 'Tous les PER' },
    { value: 10, label: 'PER < 10' },
    { value: 15, label: 'PER < 15' },
    { value: 20, label: 'PER < 20' },
];

const GROWTH_OPTIONS: { value: ProjectionFiltersState['growthTarget']; label: string }[] = [
    { value: 'any', label: 'Peu importe' },
    { value: 'div', label: '📈 Div. en hausse' },
    { value: 'rev', label: '💰 CA en hausse' },
    { value: 'res', label: '📊 Résultat en hausse' },
    { value: 'all_growth', label: '🚀 Croissance Globale' },
];

// Valeurs = reliability_tier renvoyé par l'API. L'ancienne valeur « À RISQUE » n'existait pas
// côté backend (NON_FIABLE) : le filtre « Basse » ne remontait jamais rien.
const RELIABILITY_OPTIONS: { value: ProjectionFiltersState['reliability']; label: string }[] = [
    { value: 'any', label: 'Toute fiabilité' },
    { value: 'FIABLE', label: '🟢 Fiable' },
    { value: 'MOYEN', label: '🟡 Moyenne' },
    { value: 'NON_FIABLE', label: '🔴 Non fiable' },
];

const ProjectionsFilters: React.FC<ProjectionsFiltersProps> = ({
    filters,
    onFiltersChange,
    annees,
    annee,
    onAnneeChange,
    totalResults,
    filteredResults
}) => {

    const updateFilter = <K extends keyof ProjectionFiltersState>(key: K, value: ProjectionFiltersState[K]) => {
        onFiltersChange({ ...filters, [key]: value });
    };

    const resetFilters = () => onFiltersChange(FILTRES_PAR_DEFAUT);

    const hasActiveFilters = filters.minYield > 0 || filters.maxPer !== null || filters.growthTarget !== 'any' || filters.reliability !== 'any' || filters.riskOnly;

    return (
        <div className="projections-filters-wrapper">
            <div className="projections-filters-container">

                {/* Year Filter */}
                <div className="filter-group filter-group--required">
                    <label className="filter-label">
                        Exercice <span className="filter-required">*</span>
                    </label>
                    <div className="filter-select-wrapper">
                        <select
                            value={annee ?? ''}
                            onChange={(e) => onAnneeChange(Number(e.target.value))}
                            className="filter-select"
                            disabled={annees.length === 0}
                        >
                            {annees.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        <ChevronDown className="filter-select-icon" size={14} />
                    </div>
                </div>

                {/* Yield Filter */}
                <div className="filter-group">
                    <label className="filter-label">Rendement Min.</label>
                    <div className="filter-select-wrapper">
                        <select
                            value={filters.minYield}
                            onChange={(e) => updateFilter('minYield', Number(e.target.value))}
                            className="filter-select"
                        >
                            {YIELD_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="filter-select-icon" size={14} />
                    </div>
                </div>

                {/* PER Filter */}
                <div className="filter-group">
                    <label className="filter-label">Plafond PER</label>
                    <div className="filter-select-wrapper">
                        <select
                            value={filters.maxPer ?? ''}
                            onChange={(e) => updateFilter('maxPer', e.target.value ? Number(e.target.value) : null)}
                            className="filter-select"
                        >
                            {PER_OPTIONS.map((opt) => (
                                <option key={opt.value === null ? '' : opt.value} value={opt.value === null ? '' : opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="filter-select-icon" size={14} />
                    </div>
                </div>

                {/* Growth Filter */}
                <div className="filter-group">
                    <label className="filter-label">Focus Croissance</label>
                    <div className="filter-select-wrapper">
                        <select
                            value={filters.growthTarget}
                            onChange={(e) => updateFilter('growthTarget', e.target.value as ProjectionFiltersState['growthTarget'])}
                            className="filter-select"
                        >
                            {GROWTH_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="filter-select-icon" size={14} />
                    </div>
                </div>

                {/* Reliability Filter */}
                <div className="filter-group">
                    <label className="filter-label">Fiabilité</label>
                    <div className="filter-select-wrapper">
                        <select
                            value={filters.reliability}
                            onChange={(e) => updateFilter('reliability', e.target.value as ProjectionFiltersState['reliability'])}
                            className="filter-select"
                        >
                            {RELIABILITY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="filter-select-icon" size={14} />
                    </div>
                </div>

                {/* Risk Toggle */}
                <div className="filter-group filter-group--toggle">
                    <label className="filter-label">Alertes Risque</label>
                    <div 
                        className={`filter-toggle ${filters.riskOnly ? 'filter-toggle--active' : ''}`}
                        onClick={() => updateFilter('riskOnly', !filters.riskOnly)}
                    >
                        <AlertTriangle size={14} />
                        <span>{filters.riskOnly ? 'Actif' : 'Toutes'}</span>
                    </div>
                </div>

                {/* Reset Button */}
                {hasActiveFilters && (
                    <div className="filter-group filter-group--reset">
                        <button className="filter-reset-button" onClick={resetFilters}>
                            <X size={14} /> Effacer
                        </button>
                    </div>
                )}
            </div>

            {/* Results Counter */}
            <div className="filter-results-badge">
                <Filter size={12} strokeWidth={3} />
                <span>
                    {filteredResults === totalResults
                        ? `${totalResults} Valeurs`
                        : `${filteredResults} filtrées / ${totalResults}`}
                </span>
            </div>
        </div>
    );
};

export default ProjectionsFilters;
